<template>
  <form id="gf-survey" class="main-container" v-if="survey">
    <div class="row">
      <div
        class="survey-container main-container"
        v-if="
          loadedSurvey &&
          (surveyStatus === 'ready' || surveyStatus === 'nps_finished')
        "
      >
        <div class="survey-inner">
          <survey-header
            :title="survey.title"
            :description="survey.description"
            :title-color="survey.options.baseColor"
            :description-color="survey.options.secondaryColor"
            :title-font-size="survey.options.titleFontSize"
            :description-font-size="survey.options.descriptionFontSize"
            :highlight-color="colorOptions.highlightColor"
            :img-link="survey.logo"
          ></survey-header>
          <div class="settings">
            <language-selector
              :locales="survey.locales"
              :lang="locale"
              :color="survey.options.baseColor"
            />
          </div>
          <div class="content">
            <div
              class="question-container"
              v-for="field in surveyFields"
              v-bind:key="field.id"
            >
              <field-selector
                :fields="surveyFields"
                :outOfFive="survey.options.outOfFive && !field.isNps"
                :field="field"
                :colors="colorOptions"
                @click.native="clickEvent($event, field)"
              ></field-selector>
            </div>
            <div
              class="guest-details"
              v-if="isGeneric && loadedSurvey && !pms_external_id"
            >
              <generic-form
                :infoFields="survey.infoFields"
                :lang="locale"
                :error="guestDetailsError"
                :colorOptions="colorOptions"
                @details-updated="updateGuestDetails"
              />
            </div>
            <div class="button-wrapper">
              <button
                type="button"
                :style="submitButtonStyle"
                :disabled="isSubmitting"
                class="submit-button"
                v-on:click="submitSurvey"
              >
                {{ survey.options.submitText }}{{ isSubmitting ? "..." : "" }}
              </button>
            </div>
          </div>
          <div class="bottom">
            {{ survey.options.bottomText }}
          </div>
        </div>
      </div>
      <div
        class="thank-you-message-container main-container"
        v-if="loadedSurvey && surveyStatus === 'finished'"
      >
        <survey-header
          :title="survey.title"
          :description="survey.description"
          :title-color="survey.options.baseColor"
          :description-color="survey.options.secondaryColor"
          :title-font-size="survey.options.titleFontSize"
          :description-font-size="survey.options.descriptionFontSize"
          :highlight-color="colorOptions.highlightColor"
          :img-link="survey.logo"
        ></survey-header>
        <thank-you-page
          :title="survey.title"
          :description="survey.description"
          :title-color="colorOptions.baseColor"
          :description-color="colorOptions.secondaryColor"
          :highlight-color="colorOptions.highlightColor"
          :title-font-size="survey.options.titleFontSize"
          :description-font-size="survey.options.descriptionFontSize"
          :thank-you-text="survey.options.thankYouText"
          :subscribe="survey.subscribe"
        ></thank-you-page>
      </div>
      <div
        class="side-image"
        v-bind:style="{
          'background-image': 'url(' + survey.mainImage + ')',
          height: '100%',
        }"
      ></div>
    </div>
  </form>
</template>

<script>
/* eslint-disable */
import Vue from "vue";
import fields from "survey-fields";
import SurveyActionsMixin from "../mixins/SurveyActionsMixin";
import TrackingService from "../services/TrackingService";
import GenericForm from "./GenericForm";
import LanguageSelector from "./LanguageSelector";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

Vue.component("v-select", vSelect);

Vue.use(fields);

export default {
  name: "Survey",
  mixins: [SurveyActionsMixin],
  components: {
    GenericForm,
    LanguageSelector,
  },
  computed: {
    surveyFields() {
      if (this.surveyStatus === "ready") {
        return this.sortFields(this.survey.fields);
      }

      if (this.surveyStatus === "nps_finished") {
        return this.sortFields(this.survey.fields.filter((field) => !field.isNps));
      }
    },
    submitButtonStyle() {
      return {
        backgroundColor: this.colorOptions.highlightColor,
      };
    },
    colorOptions() {
      let colorSettings = {
        baseColor: this.survey.options.baseColor,
        secondaryColor: this.survey.options.secondaryColor,
        highlightColor: this.survey.options.highlightColor,
      };

      if (!colorSettings.baseColor) {
        colorSettings.baseColor = "#2c2d4a";
      }
      if (!colorSettings.secondaryColor) {
        colorSettings.secondaryColor = "#90919e";
      }
      if (!colorSettings.highlightColor) {
        colorSettings.highlightColor = "#00aef1";
      }

      return colorSettings;
    },
  },
  methods: {
    sortFields(fields) {
      return fields.map(field => {
        if ((field.type === 'checkbox' || field.type === 'radiobutton') && Array.isArray(field.answerOptions)) {
          field.answerOptions = field.answerOptions
            .filter(option => option)
            .sort((a, b) => (a.order || 0) - (b.order || 0)); // Sort by order, defaulting to 0
        }
        return field;
      });
    },
    clickEvent(event, field) {
      this.analytics.event("clickedField", {
        field: {
          id: field.id,
          title: field.title,
        },
        value: event.target.innerText,
      });
    },
    parseJwt(token) {
      var base64Url = token.split(".")[1];
      var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      var jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );

      return JSON.parse(jsonPayload);
    },
    /**
     * To fill nps field pass id={fieldId} and value={fieldValue} to the url query string
     *
     * @param npsFieldId
     * @param npsFieldValue
     */
    fillSurveyNpsField(npsFieldValue) {
      if (isNaN(npsFieldValue) || npsFieldValue < 1 || npsFieldValue > 10)
        return false;
      const index = this.survey.fields.findIndex((field) => {
        return field.isNps;
      });
      if (index < 0) {
        return false;
      }
      const canBeFilled =
        this.survey.fields[index].type === "radiobutton" ||
        this.survey.fields[index].type === "checkbox" ||
        this.survey.fields[index].type === "range";
      if (canBeFilled) {
        const optionIndex = this.survey.fields[index].answerOptions.findIndex(
          (option) => {
            return parseInt(option.value) === parseInt(npsFieldValue);
          }
        );
        Vue.set(
          this.survey.fields[index].answerOptions[optionIndex],
          "selected",
          true
        );
      }
    },
    isTriggerFieldVisible(field, fields) {
      if (!field.triggers || field.triggers.length === 0) {
        return true;
      }

      // we should cache this to optimize performance
      // this is a hack because computed variables acted weird
      let allSelected = [];

      for (let f of fields) {
        if (f.answerOptions) {
          const selectedAnswers = f.answerOptions.filter((answer) => {
            return answer.selected === true;
          });

          for (let ans of selectedAnswers) {
            allSelected.push(ans.uuid);
          }
        }
      }
      for (let trigger of field.triggers) {
        if (allSelected.includes(trigger.uuid)) {
          return true;
        }
      }

      return false;
    },
    pickAnswersPerField(fields) {
      let instance = this;
      const answers = fields.map((field) => {
        if (
          field.type === "text" ||
          field.type === "string" ||
          field.type === "number"
        ) {
          let isVisible = instance.isTriggerFieldVisible(field, fields);
          let isRequired = field.required && isVisible;
          return {
            required: isRequired,
            fieldId: field.id,
            value: field.value,
          };
        } else {
          const selectedAnswers = field.answerOptions.filter((answer) => {
            return answer.selected === true;
          });

          let isVisible = instance.isTriggerFieldVisible(field, fields);
          let isRequired = field.required && isVisible;

          return {
            required: isRequired,
            fieldId: field.id,
            selectedAnswersIds: selectedAnswers.map((answer) => answer.id),
          };
        }
      });
      return answers;
    },
    getInvalidAnswers(answers) {
      const invalidAnswers = answers.filter((answer) => {
        const hasAnswer =
          answer.value != null ||
          (answer.selectedAnswersIds && answer.selectedAnswersIds.length);
        return answer.required && !hasAnswer;
      });

      this.survey.fields = this.survey.fields.map((field) => {
        const fieldIsInvalid = invalidAnswers.find(
          (answer) => answer.fieldId === field.id
        );
        return fieldIsInvalid
          ? Object.assign({}, field, { invalid: true })
          : Object.assign({}, field, { invalid: false });
      });

      return invalidAnswers;
    },
    guestErrors(guest) {
      if (guest.firstName.required == true && guest.firstName.value.length < 2) {
        return "First name cannot be empty.";
      }
      if (guest.lastName.required == true && guest.lastName.value.length < 2) {
        return "Last name cannot be empty.";
      }
      if (guest.email.required == true && guest.email.value.length < 2) {
        return "Email cannot be empty.";
      }
      if (
        guest.roomNumber.required == true &&
        guest.roomNumber.value.length < 1
      ) {
        return "Room number cannot be empty.";
      }

      if (guest.nationalityCode.required == true && guest.nationalityCode.value.length < 1) {
        return "Please choose your country.";
      }

      if (
        guest.checkInDate.required == true &&
        guest.checkInDate.value.length < 1
      ) {
        return "Please choose your check-in date.";
      }
      if (
        guest.checkOutDate.required == true &&
        guest.checkOutDate.value.length < 1
      ) {
        return "Please choose your check-out date.";
      }
      if (guest.checkOutDate < guest.checkInDate) {
        return "The check-out date cannot be earlier than the check-in date.";
      }
      return false;
    },
    submitSurvey() {
      this.isSubmitting = true;
      if (!this.isGeneric && this.token) {
        const jwt = this.parseJwt(this.token);
        if (jwt.user.preview === true) return;
      }
      const answers = this.pickAnswersPerField(this.surveyFields);
      const invalidAnswers = this.getInvalidAnswers(answers);
      this.formSubmitted = true;

      if (invalidAnswers.length) {
        Vue.nextTick(() => {
          const errorElement = document.getElementsByClassName("error")[0];
          errorElement.parentElement.parentElement.parentElement.parentElement.scrollIntoView(
            { behavior: "smooth" }
          );
          invalidAnswers.forEach((answer) => {
            this.analytics.event("failedValidation", {
              field: { id: answer.fieldId },
            });
          });
        });
        this.isSubmitting = false;
      }

      let error = false;

      if (Object.keys(this.guest).length > 0) {
        error = this.guestErrors(this.guest);
      }

      if (this.isGeneric && error) {
        this.guestDetailsError = error;
        this.isSubmitting = false;
        return;
      } else {
        this.guestDetailsError = "";
      }

      const answersToPost = answers.map((answer) => {
        delete answer.required;
        return answer;
      });

      if (!invalidAnswers.length && this.submitRequest) {
        this.analytics.event("surveySubmitted", {});

        this.submitRequest(answersToPost)
          .then((res) =>
            this.performActions(
              "after",
              this.survey.actions,
              res.applicableActions
            )
          )
          .catch((error) => {
            if (
              error.response &&
              error.response.data &&
              error.response.data.error.email
            ) {
              this.guestDetailsError = "The email you entered is not valid";
            }
            this.isSubmitting = false;
          });
      }
    },
    updateGuestDetails(guest) {
      this.guest = guest;
    },
  },
  mounted() {
    this.slug = this.$route.params.slug;
    this.token = this.$route.params.jwt;
    this.pms_external_id = this.$route.query.token;
    this.anonymousGuest = this.$route.query.g ? this.$route.query.g : null;

    if (this.token) {
      const user = this.parseJwt(this.token).user;
      if (user.preview !== true) {
        this.isGeneric = true;
      }
      this.analytics = new TrackingService(this.token);
    } else if (this.anonymousGuest) {
      this.analytics = {
        event: (val) => {
          /* */
        },
        trackScrolling: (val) => {
          /* */
        },
      };
    } else {
      this.isGeneric = true;
      this.analytics = {
        event: (val) => {
          /* */
        },
        trackScrolling: (val) => {
          /* */
        },
      };
    }

    this.locale = this.$route.query.locale ? this.$route.query.locale : "eng";
    if (this.getSurvey) {
      this.getSurvey(this.slug, this.token, this.locale, this.anonymousGuest);
    }
  },

  data() {
    return {
      locale: "eng",
      loadedSurvey: false,
      surveyStatus: null,
      survey: null,
      formSubmitted: false,
      isSubmitting: false,
      groups: null,
      isGeneric: false,
      guest: {},
      anonymousGuest: null,
      pms_external_id: null,
      guestDetailsError: "",
    };
  },
};
</script>

<style>
@import "../../node_modules/survey-fields/dist/css/build.css";
.submit-button {
  padding: 10px 15px;
  font-weight: bold;
  border: 0;
  color: #fff;
  background: #00aef1;
  cursor: pointer;
  border-radius: 15px;
  min-width: 200px;
}
.submit-button:disabled {
  background: #ccc !important;
}
.button-wrapper {
  text-align: center;
  margin-top: 15px;
}
.submit-button:hover {
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.15);
}
.bottom {
  margin-bottom: 20px;
}
.side-image {
  height: 100%;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
}
.settings {
  display: flex;
  justify-content: flex-end;
}
</style>



// WEBPACK FOOTER //
// src/components/Survey.vue